import React, { useState, useEffect, Fragment } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';
import { useTranslation } from 'react-i18next';
import ForgotPasswordDialog from './ForgotPasswordDialog';

const LoginPage: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { setHeaderTheme } = useHeader();
    const [message, setMessage] = useState<{text: string; type: 'success' | 'error'} | null>(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    useEffect(() => {
        setHeaderTheme({
            bgColor: 'bg-gradient-to-b from-blue-500/95 to-blue-500/80 backdrop-blur-sm',
            textColor: 'text-white'
        });
        
        return () => {
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        // 检查是否有注册成功的消息
        const state = location.state as { message?: string; type?: 'success' | 'error' };
        if (state?.message) {
            setMessage({
                text: state.message,
                type: state.type || 'success'
            });
            // 清除 location state
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await signInWithEmailAndPassword(auth, email, password);
            handleLoginSuccess();
        } catch (error: any) {
            switch (error.code) {
                case 'auth/invalid-email':
                    setError(t('auth.loginPage.errors.invalidEmail'));
                    break;
                case 'auth/user-not-found':
                    setError(t('auth.loginPage.errors.userNotFound'));
                    break;
                case 'auth/wrong-password':
                    setError(t('auth.loginPage.errors.wrongPassword'));
                    break;
                default:
                    setError(t('auth.loginPage.errors.default'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            handleLoginSuccess();
        } catch (error: any) {
            setError(t('auth.loginPage.errors.googleSignIn'));
        } finally {
            setLoading(false);
        }
    };

    // 登录成功后的处理
    const handleLoginSuccess = () => {
        // 检查是否有报名重定向信息
        const registrationRedirect = sessionStorage.getItem('registrationRedirect');
        if (registrationRedirect) {
            try {
                const { programId, courseId } = JSON.parse(registrationRedirect);
                // 清除会话存储中的重定向信息
                sessionStorage.removeItem('registrationRedirect');
                // 重定向到报名页面
                navigate('/registration/new', { 
                    state: { programId, courseId }
                });
                return;
            } catch (error) {
                console.error('Error parsing registration redirect:', error);
            }
        }
        
        // 检查是否有课程报名重定向信息
        const enrollmentRedirect = sessionStorage.getItem('enrollmentRedirect');
        if (enrollmentRedirect) {
            try {
                const { programId, courseId } = JSON.parse(enrollmentRedirect);
                // 清除会话存储中的重定向信息
                sessionStorage.removeItem('enrollmentRedirect');
                // 重定向到项目详情页面
                navigate(`/program/${programId}`);
                return;
            } catch (error) {
                console.error('Error parsing enrollment redirect:', error);
            }
        }
        
        // 如果没有特定的重定向，则使用 location state 中的 from 路径或默认路径
        const from = location.state?.from || '/profile';
        navigate(from);
    };

    return (
        <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-200 via-blue-400 to-blue-500">
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:32px_32px]" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-transparent" />
            <div className="relative min-h-screen flex justify-center pt-48">
                <div className="w-full max-w-md px-4">
                    {message && (
                        <div className={`mb-4 rounded-lg p-4 ${
                            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                            {message.text}
                        </div>
                    )}
                    {/* Logo 区域 */}
                    <div className="text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-blue-800">
                            {t('auth.loginPage.title')}
                            <span className="block text-sm font-normal text-gray-600 mt-1">
                                {t('auth.loginPage.subtitle')}
                            </span>
                        </h2>
                    </div>

                    {/* 表单区域 */}
                    <div className="mt-8">
                        <div className="bg-white/80 backdrop-blur-xl p-8 shadow-xl ring-1 ring-black/5 rounded-2xl">
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {/* 邮箱输入框 */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('auth.loginPage.emailLabel')}
                                    </label>
                                    <div className="relative mt-2">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="block w-full rounded-lg border-0 py-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            placeholder={t('auth.loginPage.emailPlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* 密码输入框 */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('auth.loginPage.passwordLabel')}
                                    </label>
                                    <div className="relative mt-2">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type="password"
                                            autoComplete="current-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="block w-full rounded-lg border-0 py-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            placeholder={t('auth.loginPage.passwordPlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* 错误提示 */}
                                {error && (
                                    <div className="rounded-lg bg-red-400/10 p-4 backdrop-blur-sm">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-red-300">{error}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 登录按钮 */}
                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex w-full justify-center items-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 px-3 py-3 text-sm font-semibold text-white shadow-sm hover:from-blue-400 hover:to-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('auth.loginPage.signingIn')}
                                            </>
                                        ) : (
                                            t('auth.loginPage.signInButton')
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* 分割线 */}
                            <div className="relative mt-8">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-black/10"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                </div>
                            </div>

                            {/* 谷歌登录按钮 */}
                            <div className="mt-6">
                                <button
                                    onClick={handleGoogleSignIn}
                                    disabled={loading}
                                    className="flex w-full items-center justify-center gap-3 rounded-lg bg-white/10 px-3 py-3 text-sm font-semibold text-gray-600 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                                >
                                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                                        <path
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            fill="#4285F4"
                                        />
                                        <path
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            fill="#34A853"
                                        />
                                        <path
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            fill="#FBBC05"
                                        />
                                        <path
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            fill="#EA4335"
                                        />
                                    </svg>
                                    {t('auth.loginPage.googleSignIn')}
                                </button>
                            </div>

                            {/* 注册链接 */}
                            <p className="mt-8 text-center text-sm text-gray-600">
                                {t('auth.loginPage.noAccount')}{' '}
                                <Link to="/register" className="font-semibold text-gray-900 hover:text-gray-800">
                                    {t('auth.loginPage.register')}
                                </Link>
                            </p>

                            {/* 在密码输入框下方添加忘记密码链接 */}
                            <div className="flex items-center justify-center mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowForgotPassword(true)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                                >
                                    {t('auth.loginPage.forgotPassword')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 重置密码对话框 */}
            <ForgotPasswordDialog
                open={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />
        </div>
    );
};

export default LoginPage; 